import React, {FC, Dispatch, SetStateAction, useState, useEffect, useMemo} from 'react';
import {CustomModal} from '../../../../../components/UI/AntCustom/CustomModal';
import {useIntl} from 'react-intl';
import styled, {createGlobalStyle} from 'styled-components';
import {CloseOutlined} from '@ant-design/icons';
import {formatDate, parseStringAsUTCDate} from '../../../../../utils/helpers/dateHelpers/dateHelpers';
import {DisplayAirport} from '../../../../../utils/helpers/textDisplayHelpers/DisplayAirport';
import RouteArrowIcon from '../../../../../media/icons/route_arrow.svg?react';
import {VerticalDivider} from '../../../../../components/UI/VerticalDivider/VerticalDivider';
import {CommonDetailsMessages} from '../../../../../intl/commonMessages/CommonDetailsMessages';
import {DebounceSelect} from '../../../../../components/UI/AntCustom/DebounceSelect';
import {fetchMessageTemplates, IMessageTemplateOption} from '../../../../../utils/helpers/selectDataFetchers/fetchMessageTemplates';
import {Checkbox} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import {CustomButton} from '../../../../../components/UI/AntCustom/CustomButton';
import {CustomFieldInput} from '../../../../../components/UI/AntCustom/CustomFieldInput';
import {fetchInitialAirportForSelect} from '../../../../../utils/helpers/selectDataFetchers/fetchAirports';
import {API} from '../../../../../API/API';
import {AirportApiGetAirportRequest, BookingApiBrokerSubmitRequest, BrokerAircraftEntity, BookingApiPlaneCategoryListRequest, PageModel, FlightLegEntity} from '../../../../../proto/generated/api_entities_pb';
import {mapAirportEntityToIAirport} from '../../../../../store/reducers/fleetReducer/fleetMappers';
import {useAppSelector} from '../../../../../store/store';
import {getLocalDateAsISOString} from '../../../../../utils/helpers/dateHelpers/dateHelpers';
import {message} from 'antd';
import {getErrorMessageByCode} from '../../../../../utils/constans/errorCodes/errorCodes';
import {NewTripRequestPageMessages} from '../NewTripRequestPageMessages';
import {InitialPage} from '../../../../../utils/constans/pagination/pagination';
import {PageModelLimits} from '../../../../../utils/constans/pagination/pageModelLimits';

const StyledModal = styled(CustomModal)`
  .ant-modal-content {
    border-radius: 20px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-body {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 60px);
  }
`;

// Добавляем уникальный className для модалки, чтобы ограничить стили
const MODAL_CLASS_NAME = 'flight-request-confirmation-modal';

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 12px 32px;
  border-bottom: 1px solid #dddddc;
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 22px;
  color: #000;
`;

const CloseButton = styled.div`
  width: 16px;
  height: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c8c8b;
  
  &:hover {
    color: #000;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  
  .ant-form-item-label {
    overflow: visible !important;
    height: auto !important;
    min-height: 17px !important;
    display: flex !important;
    align-items: center !important;
    width: 70px !important;
    flex-shrink: 0 !important;
  }
  
  .ant-form-item-label > label {
    height: auto !important;
    min-height: 17px !important;
    overflow: visible !important;
    white-space: normal !important;
    display: flex !important;
    align-items: center !important;
    margin-right: 10px !important;
    
    &::after {
      display: none !important;
    }
  }
  
  .ant-form-item.message-field .ant-form-item-label {
    align-items: flex-start !important;
  }
  
  .ant-form-item-label > label > span {
    overflow: visible !important;
    text-overflow: clip !important;
  }
`;

const ModalFooter = styled.div`
  padding: 24px 32px 32px 32px;
`;

const ItineraryCard = styled.div`
  background: #FFFFFF;
  border: none;
  border-radius: 0;
  padding: 16px 24px;
  position: relative;
`;

const ItineraryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

const ItineraryHeaderRow = styled.div`
  display: flex;
  gap: 0;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media (max-width: 630px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ItineraryVerticalDivider = styled(VerticalDivider)`
  @media (max-width: 630px) {
    display: none;
  }
`;

const ItineraryRouteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  align-items: center;
  justify-content: center;

  @media (max-width: 630px) {
    width: 100%;
    min-width: 100%;
    gap: 16px;
  }
`;

const RouteFromToWrapper = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  position: relative;

  @media (max-width: 540px) {
    flex-direction: column;
  }
`;

const RouteLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const RouteAirportCode = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.2px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const RouteAirportCodeText = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.2px;
`;

const RouteDateTimeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

const RouteDateTimeText = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #000000;
  opacity: 0.5;
`;

const RouteArrowWithPax = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const RoutePaxLabel = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #000000;
  opacity: 0.5;
`;

const RouteFromToItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
`;

const RouteFromToLabel = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  opacity: 0.5;
`;

const RouteFromToDate = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  opacity: 0.5;
`;

const PaxRequestedSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 60px;
  flex-shrink: 0;
`;

const PaxRequestedLabel = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  opacity: 0.5;
`;

const PaxRequestedCount = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: #000000;
`;

const RouteAirportLocation = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 22px;
  color: #000000;
  opacity: 0.5;
`;

const RouteArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const JetsRequestedSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const JetsRequestedLabel = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  opacity: 0.5;
  letter-spacing: -0.14px;
  white-space: nowrap;
`;

const JetsRequestedCount = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.2;
  color: #000000;
  letter-spacing: -0.16px;
`;

const MessageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledTextArea = styled(TextArea)`
  border-radius: 5px !important;
  border-color: #D9DAD9 !important;
`;

const CheckboxWrapper = styled.div`
  margin-top: 16px;
`;

const SendButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  width: 100%;
`;

// Глобальные стили для dropdown в модалке, чтобы опции не обрезались
// Примечание: createGlobalStyle применяет стили глобально, но они активны только когда компонент рендерится
// Когда модалка закрыта, компонент может быть размонтирован, и стили перестанут применяться
const FlightRequestConfirmationDropdownStyles = createGlobalStyle`
  /* Стили для выпадающего меню селекторов в модалке Flight Request Confirmation */
  /* Эти стили применяются ко всем dropdown на странице, когда модалка открыта */
  .ant-select-dropdown {
    /* Минимальный padding-bottom, чтобы последняя опция не обрезалась */
    padding-bottom: 4px !important;
    
    .ant-select-item {
      /* Разрешаем многострочный текст в опциях */
      min-height: auto !important;
      height: auto !important;
      line-height: 1.5 !important;
      padding: 8px 12px !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      
      .ant-select-item-option-content {
        /* Разрешаем многострочный текст в контенте опции */
        white-space: normal !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        line-height: 1.5 !important;
      }
    }
    
    /* Стили для контейнера с опциями (rc-virtual-list-holder) */
    .rc-virtual-list-holder {
      /* Минимальный padding-bottom для последней опции, чтобы она не обрезалась */
      padding-bottom: 4px !important;
      /* Убеждаемся, что контейнер не обрезает содержимое */
      overflow-y: auto !important;
    }
    
    /* Стили для виртуализированного списка */
    .rc-virtual-list {
      /* Убеждаемся, что виртуализация не обрезает последний элемент */
      padding-bottom: 0 !important;
    }
    
    /* Стили для внутреннего контейнера виртуализации */
    .rc-virtual-list-holder-inner {
      /* Минимальный padding-bottom для последней опции */
      padding-bottom: 4px !important;
    }
    
    /* Стили для меню опций (для невиртуализированных списков) */
    .ant-select-dropdown-menu {
      /* Минимальный padding-bottom для последней опции */
      padding-bottom: 4px !important;
    }
    
    /* Дополнительная защита для последнего элемента */
    .ant-select-item:last-child {
      margin-bottom: 0 !important;
    }
    
    /* Убеждаемся, что dropdown не обрезает последний элемент */
    .ant-select-dropdown-menu-item:last-child {
      margin-bottom: 0 !important;
    }
    
    /* Для невиртуализированных списков - убеждаемся, что последний элемент виден */
    &:not(.rc-virtual-list) {
      .ant-select-dropdown-menu {
        padding-bottom: 20px !important;
      }
    }
  }
`;

interface IFlightRequestConfirmationModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  searchParams?: {
    airportFromId?: number;
    airportToId?: number;
    departureDate?: Date;
    departureTime?: string;
    pax?: number;
    paxTo?: number; // PAX count for return flight (for round trips)
    multilegRoutes?: Array<{
      airportFromId: number;
      airportToId: number;
      departureDate: string; // YYYY-MM-DD
      departureTime?: string; // HH:MM
      pax: number;
    }>; // For multileg flights: all legs with their PAX counts
    companionsAllowed?: boolean;
    airportFrom?: {
      code: string;
      location_and_name: string;
    };
    airportTo?: {
      code: string;
      location_and_name: string;
    };
    timezoneOffset?: number;
  };
  selectedFlightsCount?: number;
  selectedFlightIds?: Set<string>; // ID выбранных самолетов
  onRequestSubmitted?: () => void; // Callback после успешной отправки запроса
}

export const FlightRequestConfirmationModal: FC<IFlightRequestConfirmationModal> = ({
  isModalVisible,
  setIsModalVisible,
  searchParams,
  selectedFlightsCount = 0,
  selectedFlightIds = new Set(),
  onRequestSubmitted,
}) => {
  const intl = useIntl();
  const [messageText, setMessageText] = useState<string>('');
  const [sendBcc, setSendBcc] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IMessageTemplateOption | null>(null);
  const [templateOptions, setTemplateOptions] = useState<IMessageTemplateOption[]>([]); // Сохраняем загруженные опции
  const [airportFrom, setAirportFrom] = useState<{code: string; location_and_name: string; timezoneOffset?: number} | null>(null);
  const [airportTo, setAirportTo] = useState<{code: string; location_and_name: string; timezoneOffset?: number} | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Store airport info for all multileg routes
  const [multilegAirports, setMultilegAirports] = useState<Record<number, {code: string; location_and_name: string; timezoneOffset?: number}>>({});
  
  // Получаем полные объекты самолетов из reducer
  const brokerAircraftEntities = useAppSelector(state => state.newRequest.brokerAircraftEntities || []);

  const handleClose = () => {
    setIsModalVisible(false);
    setMessageText('');
    setSendBcc(false);
    setSelectedTemplate(null);
    setTemplateOptions([]); // Очищаем сохраненные опции
    setAirportFrom(null);
    setAirportTo(null);
    setMultilegAirports({});
  };

  const handleTemplateChange = (value: IMessageTemplateOption | IMessageTemplateOption[] | null) => {
    const template = Array.isArray(value) ? value[0] : value;
    setSelectedTemplate(template || null);
    
    // Если выбран шаблон, ищем его полный объект в сохраненных опциях по value
    if (template && template.value !== undefined) {
      // Ищем полный объект шаблона в сохраненных опциях
      const fullTemplate = templateOptions.find(opt => opt.value === template.value);
      
      if (fullTemplate && fullTemplate.text) {
        setMessageText(fullTemplate.text);
      } else {
        setMessageText('');
      }
    } else {
      setMessageText('');
    }
  };

  const handleSend = async () => {
    if (!searchParams || !searchParams.airportFromId || !searchParams.airportToId || !searchParams.pax) {
      message.error(intl.formatMessage({id: 'newTripRequestPage.missingParams', defaultMessage: 'Отсутствуют необходимые параметры'}));
      return;
    }

    if (selectedFlightIds.size === 0) {
      message.error(intl.formatMessage({id: 'newTripRequestPage.noFlightsSelected', defaultMessage: 'Не выбраны самолеты'}));
      return;
    }

    // Находим полные объекты выбранных самолетов
    const selectedAircraftEntities = brokerAircraftEntities.filter((entity: BrokerAircraftEntity) => {
      const planeId = entity.getPlane()?.getPlaneid();
      return planeId && selectedFlightIds.has(String(planeId));
    });

    if (selectedAircraftEntities.length === 0) {
      message.error(intl.formatMessage({id: 'newTripRequestPage.aircraftNotFound', defaultMessage: 'Не найдены данные выбранных самолетов'}));
      return;
    }

    setIsSubmitting(true);
    try {
      const req = new BookingApiBrokerSubmitRequest()
        .setPlanesList(selectedAircraftEntities)
        .setOriginairportid(searchParams.airportFromId)
        .setDestinationairportid(searchParams.airportToId)
        .setPax(searchParams.pax)
        .setComment(messageText || '');

      if (searchParams.departureDate) {
        req.setDeparturedate(getLocalDateAsISOString(searchParams.departureDate));
      }
      if (searchParams.departureTime) {
        req.setDeparturetime(searchParams.departureTime);
      }

      // Получаем категории самолетов из выбранных самолетов
      const planeCategoryIds = new Set<number>();
      selectedAircraftEntities.forEach((entity: BrokerAircraftEntity) => {
        const categoryId = entity.getPlane()?.getPlanetype()?.getPlanecategory()?.getPlanecategoryid();
        if (categoryId) {
          planeCategoryIds.add(categoryId);
        }
      });
      
      // Если категории не найдены в выбранных самолетах, получаем все категории
      let finalCategoryIds: number[] = [];
      if (planeCategoryIds.size > 0) {
        finalCategoryIds = Array.from(planeCategoryIds);
      } else {
        // Получаем все категории, если ничего не выбрано
        const categoryListReq = new BookingApiPlaneCategoryListRequest()
          .setPagemodel(
            new PageModel().setPage(InitialPage).setLimit(PageModelLimits.Select)
          );
        const categoryListResponse = await API.getPlaneCategoryList(categoryListReq);
        finalCategoryIds = categoryListResponse.getPlanecategoriesList().map(pc => pc.getPlanecategoryid());
      }
      
      if (finalCategoryIds.length > 0) {
        req.setPlanecategoryidsList(finalCategoryIds);
      }

      // Устанавливаем companionsAllowed из searchParams
      if (searchParams.companionsAllowed !== undefined) {
        req.setCompanionsallowed(searchParams.companionsAllowed);
      }

      // Устанавливаем paxTo если есть (для round-trip)
      if (searchParams.paxTo !== undefined && searchParams.paxTo > 0) {
        req.setPaxto(searchParams.paxTo);
      }

      // Устанавливаем multilegRoutes если есть
      if (searchParams.multilegRoutes && searchParams.multilegRoutes.length > 0) {
        const multilegRoutesList = searchParams.multilegRoutes.map(leg => {
          const legEntity = new FlightLegEntity()
            .setAirportfromid(leg.airportFromId)
            .setAirporttoid(leg.airportToId)
            .setDeparturedate(leg.departureDate)
            .setPax(leg.pax);
          if (leg.departureTime) {
            legEntity.setDeparturetime(leg.departureTime);
          }
          return legEntity;
        });
        req.setMultilegroutesList(multilegRoutesList);
      }

      // Поля needAirAmbulance, needAnimalsAboard и т.д. пока не заполняются в формах,
      // поэтому устанавливаем их в false по умолчанию
      req.setNeedairambulance(false);
      req.setNeedanimalsaboard(false);
      req.setNeedsmokingaboard(false);
      req.setNeedaircargofreight(false);

      const response = await API.brokerSubmitRequest(req);
      
      if (response.getResultresponse()?.getIssuccessful()) {
        message.success(intl.formatMessage(NewTripRequestPageMessages.requestSubmitted));
        handleClose();
        // Вызываем callback для очистки формы и результатов поиска
        if (onRequestSubmitted) {
          onRequestSubmitted();
        }
        // Можно перенаправить на страницу с заказами или обновить список
      } else {
        const errorCode = response.getResultresponse()?.getErrormessage();
        const errorMsg = errorCode ? getErrorMessageByCode(errorCode) : intl.formatMessage(NewTripRequestPageMessages.submitError);
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      message.error(intl.formatMessage(NewTripRequestPageMessages.submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Получаем информацию об аэропортах при открытии модалки
  useEffect(() => {
    if (isModalVisible && searchParams) {
      const loadAirports = async () => {
        // If multileg routes exist, load all airports from all legs
        if (searchParams.multilegRoutes && searchParams.multilegRoutes.length > 0) {
          const airportIds = new Set<number>();
          searchParams.multilegRoutes.forEach(leg => {
            airportIds.add(leg.airportFromId);
            airportIds.add(leg.airportToId);
          });
          
          const newMultilegAirports: Record<number, {code: string; location_and_name: string; timezoneOffset?: number}> = {};
          
          await Promise.all(
            Array.from(airportIds).map(async (airportId) => {
              try {
                const req = new AirportApiGetAirportRequest().setAirportid(airportId);
                const airportEntity = (await API.getAirportById(req)).getAirport();
                if (airportEntity) {
                  const airport = mapAirportEntityToIAirport(airportEntity);
                  newMultilegAirports[airportId] = {
                    code: airport.icaoCode || airport.iataCode || '',
                    location_and_name: DisplayAirport(airport, 'location_and_name'),
                    timezoneOffset: airport.timezone?.offset || 0,
                  };
                }
              } catch (error) {
                console.error(`Failed to load airport ${airportId}:`, error);
              }
            })
          );
          
          setMultilegAirports(newMultilegAirports);
          
          // Also set airportFrom and airportTo for backward compatibility
          const firstLeg = searchParams.multilegRoutes[0];
          if (firstLeg.airportFromId && newMultilegAirports[firstLeg.airportFromId]) {
            setAirportFrom(newMultilegAirports[firstLeg.airportFromId]);
          }
          const lastLeg = searchParams.multilegRoutes[searchParams.multilegRoutes.length - 1];
          if (lastLeg.airportToId && newMultilegAirports[lastLeg.airportToId]) {
            setAirportTo(newMultilegAirports[lastLeg.airportToId]);
          }
        } else {
          // Single leg: load origin and destination
          if (searchParams.airportFromId) {
            try {
              const req = new AirportApiGetAirportRequest().setAirportid(searchParams.airportFromId);
              const airportEntity = (await API.getAirportById(req)).getAirport();
              if (airportEntity) {
                const airport = mapAirportEntityToIAirport(airportEntity);
                setAirportFrom({
                  code: airport.icaoCode || airport.iataCode || '',
                  location_and_name: DisplayAirport(airport, 'location_and_name'),
                  timezoneOffset: airport.timezone?.offset || 0,
                });
              }
            } catch (error) {
              console.error('Failed to load airport from:', error);
            }
          }
          if (searchParams.airportToId) {
            try {
              const req = new AirportApiGetAirportRequest().setAirportid(searchParams.airportToId);
              const airportEntity = (await API.getAirportById(req)).getAirport();
              if (airportEntity) {
                const airport = mapAirportEntityToIAirport(airportEntity);
                setAirportTo({
                  code: airport.icaoCode || airport.iataCode || '',
                  location_and_name: DisplayAirport(airport, 'location_and_name'),
                  timezoneOffset: airport.timezone?.offset || 0,
                });
              }
            } catch (error) {
              console.error('Failed to load airport to:', error);
            }
          }
        }
      };
      loadAirports();
    }
  }, [isModalVisible, searchParams]);

  // Format date in format: "Wed, 31 Jun 2022 (09:00 UTC)"
  const formatDateWithWeekday = (date: Date, timezoneOffset: number = 0): string => {
    const adjustedDate = new Date(date.getTime() + timezoneOffset * 60000);
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const weekday = weekdays[adjustedDate.getUTCDay()];
    const day = adjustedDate.getUTCDate();
    const month = months[adjustedDate.getUTCMonth()];
    const year = adjustedDate.getUTCFullYear();
    const hours = adjustedDate.getUTCHours().toString().padStart(2, '0');
    const minutes = adjustedDate.getUTCMinutes().toString().padStart(2, '0');
    
    return `${weekday}, ${day} ${month} ${year} (${hours}:${minutes} UTC)`;
  };

  // Используем данные из searchParams или fallback на тестовые данные
  const dateFrom = searchParams?.departureDate || new Date();
  const timezoneOffsetFrom = airportFrom?.timezoneOffset || searchParams?.timezoneOffset || 0;
  const timezoneOffsetTo = airportTo?.timezoneOffset || searchParams?.timezoneOffset || 0;
  const airportFromData = airportFrom || (searchParams?.airportFrom ? { ...searchParams.airportFrom, timezoneOffset: timezoneOffsetFrom } : { code: '', location_and_name: '', timezoneOffset: 0 });
  const airportToData = airportTo || (searchParams?.airportTo ? { ...searchParams.airportTo, timezoneOffset: timezoneOffsetTo } : { code: '', location_and_name: '', timezoneOffset: 0 });
  const jetsRequested = selectedFlightsCount || 0;

  // Calculate earliest departure time from selected aircraft (in UTC)
  const earliestDepartureTime = useMemo(() => {
    if (selectedFlightIds.size === 0 || brokerAircraftEntities.length === 0) {
      return null;
    }

    const selectedAircraft = brokerAircraftEntities.filter((entity: BrokerAircraftEntity) => {
      const planeId = entity.getPlane()?.getPlaneid();
      return planeId && selectedFlightIds.has(String(planeId));
    });

    let earliest: Date | null = null;

    selectedAircraft.forEach((entity: BrokerAircraftEntity) => {
      let departureDateTimeStr: string | null = null;
      
      // Use adjustedDepartureDateTime if available
      if (entity.getAdjusteddeparturedatetime()) {
        departureDateTimeStr = entity.getAdjusteddeparturedatetime();
      } else {
        // Otherwise use requested departure time
        if (searchParams?.multilegRoutes && searchParams.multilegRoutes.length > 0) {
          // For multileg, use first leg's departure date/time
          const firstLeg = searchParams.multilegRoutes[0];
          if (firstLeg.departureDate) {
            const timeStr = firstLeg.departureTime || '00:00:00';
            departureDateTimeStr = `${firstLeg.departureDate} ${timeStr}`;
          }
        } else if (searchParams?.departureDate && searchParams?.departureTime) {
          // For single leg, use departure date/time
          let dateStr: string;
          if (searchParams.departureDate instanceof Date) {
            dateStr = searchParams.departureDate.toISOString().split('T')[0];
          } else {
            // If it's a string, use it directly
            dateStr = String(searchParams.departureDate);
          }
          departureDateTimeStr = `${dateStr} ${searchParams.departureTime}`;
        }
      }
      
      if (departureDateTimeStr) {
        try {
          const parsed = parseStringAsUTCDate(departureDateTimeStr);
          if (parsed && (!earliest || parsed < earliest)) {
            earliest = parsed;
          }
        } catch (e) {
          console.error('Error parsing departure time:', e);
        }
      }
    });

    return earliest;
  }, [selectedFlightIds, brokerAircraftEntities, searchParams?.departureDate, searchParams?.departureTime, searchParams?.multilegRoutes]);

  // Format earliest departure time in UTC
  const formatEarliestDeparture = (date: Date): string => {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const weekday = weekdays[date.getUTCDay()];
    const day = date.getUTCDate();
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    
    return `${weekday}, ${day} ${month} ${year} ${hours}:${minutes} UTC`;
  };

  // Format date in DD.MM.YY format
  const formatDateShort = (date: Date, timezoneOffset: number = 0): string => {
    const localDate = new Date(date.getTime() + timezoneOffset * 60000);
    const day = localDate.getUTCDate().toString().padStart(2, '0');
    const month = (localDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = (localDate.getUTCFullYear() % 100).toString().padStart(2, '0');
    return `${day}.${month}.${year}`;
  };

  // Format departure time in UTC
  const formatDepartureTimeUTC = (date: Date): string => {
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes} UTC`;
  };

  // Format departure time in local timezone
  const formatDepartureTimeLocal = (date: Date, timezoneOffset: number): string => {
    const localDate = new Date(date.getTime() + timezoneOffset * 60000);
    const hours = localDate.getUTCHours().toString().padStart(2, '0');
    const minutes = localDate.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes} Local time`;
  };

  // Format departure time with date if date differs from previous date
  const formatDepartureTimeWithDate = (date: Date, previousDate: Date | null, timezoneOffset: number): string => {
    const timeStr = formatDepartureTimeUTC(date);
    
    // Check if dates differ (comparing date parts only, ignoring time)
    if (previousDate) {
      const currentDateOnly = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
      const previousDateOnly = new Date(Date.UTC(previousDate.getUTCFullYear(), previousDate.getUTCMonth(), previousDate.getUTCDate()));
      
      if (currentDateOnly.getTime() !== previousDateOnly.getTime()) {
        // Dates differ, include date in format
        const dateStr = formatDateShort(date, timezoneOffset);
        return `${dateStr} ${timeStr}`;
      }
    }
    
    return timeStr;
  };

  // Parse departure date and time into a Date object
  const parseDepartureDateTime = (departureDate: string | Date | undefined, departureTime?: string): Date | null => {
    if (!departureDate) return null;
    
    // If departureDate is a Date object and has time information, and no departureTime is provided, use the Date's time
    if (departureDate instanceof Date && !departureTime) {
      // Check if the date has non-midnight time
      const hours = departureDate.getUTCHours();
      const minutes = departureDate.getUTCMinutes();
      if (hours !== 0 || minutes !== 0) {
        // Date already has time information, use it directly
        return departureDate;
      }
    }
    
    let dateStr: string;
    if (departureDate instanceof Date) {
      // If it's a Date object, convert to ISO string (date part only)
      dateStr = departureDate.toISOString().split('T')[0];
    } else {
      dateStr = departureDate;
    }
    
    // Handle time string - it might be "HH:MM" or "HH:MM:SS" or undefined
    let timeStr: string;
    if (!departureTime || departureTime.trim() === '') {
      timeStr = '00:00:00';
    } else if (departureTime.includes(':')) {
      // If it already has seconds, use as is, otherwise add :00
      const parts = departureTime.split(':');
      if (parts.length === 2) {
        timeStr = `${departureTime}:00`; // Add seconds if missing
      } else if (parts.length === 3) {
        timeStr = departureTime; // Already has seconds
      } else {
        timeStr = '00:00:00'; // Invalid format, default to midnight
      }
    } else {
      timeStr = '00:00:00';
    }
    
    const dateTimeStr = `${dateStr} ${timeStr}`;
    
    try {
      return parseStringAsUTCDate(dateTimeStr);
    } catch (e) {
      console.error('Error parsing departure date/time:', { departureDate, departureTime, dateTimeStr }, e);
      return null;
    }
  };

  return (
    <>
      {/* Стили применяются только когда модалка открыта */}
      {isModalVisible && <FlightRequestConfirmationDropdownStyles />}
      <StyledModal
        open={isModalVisible}
        onCancel={handleClose}
        footer={null}
        width={900}
        centered
        closable={false}
        className={MODAL_CLASS_NAME}
      >
        <ModalHeader>
        <ModalTitle>
          {intl.formatMessage({id: 'newTripRequestPage.flightRequestConfirmation', defaultMessage: 'Flight Request Confirmation'})}
        </ModalTitle>
        <CloseButton onClick={handleClose}>
          <CloseOutlined />
        </CloseButton>
      </ModalHeader>

      <ModalContent>
        <ItineraryCard>
          <ItineraryContent>
            <ItineraryHeaderRow>
              <JetsRequestedSection>
                <JetsRequestedLabel>
                  {intl.formatMessage({id: 'newTripRequestPage.jetsRequested', defaultMessage: 'Jets requested'})}
                </JetsRequestedLabel>
                <JetsRequestedCount>{jetsRequested}</JetsRequestedCount>
                {earliestDepartureTime && (
                  <>
                    <JetsRequestedLabel>
                      {intl.formatMessage({id: 'newTripRequestPage.earliestFrom', defaultMessage: 'Earliest from'})}:
                    </JetsRequestedLabel>
                    <JetsRequestedCount style={{fontSize: '14px'}}>
                      {formatEarliestDeparture(earliestDepartureTime)}
                    </JetsRequestedCount>
                  </>
                )}
              </JetsRequestedSection>
            </ItineraryHeaderRow>

            <ItineraryRouteSection>
              {searchParams?.multilegRoutes && searchParams.multilegRoutes.length > 0 ? (
                // Display all multileg routes in one line: A → B → C → D
                <RouteLine>
                  {searchParams.multilegRoutes.map((leg, index) => {
                    const fromAirport = multilegAirports[leg.airportFromId] || { code: `ID:${leg.airportFromId}`, location_and_name: `Airport ${leg.airportFromId}`, timezoneOffset: 0 };
                    const toAirport = multilegAirports[leg.airportToId] || { code: `ID:${leg.airportToId}`, location_and_name: `Airport ${leg.airportToId}`, timezoneOffset: 0 };
                    
                    // Parse departure date and time for this leg
                    const departureDateTime = parseDepartureDateTime(leg.departureDate, leg.departureTime);
                    
                    return (
                      <React.Fragment key={index}>
                        {/* Show origin airport only for first leg */}
                        {index === 0 && (
                          <RouteAirportCode>
                            <RouteAirportCodeText>{fromAirport.code}</RouteAirportCodeText>
                            {departureDateTime && (
                              <RouteDateTimeInfo>
                                <RouteDateTimeText>
                                  {formatDepartureTimeUTC(departureDateTime)}
                                </RouteDateTimeText>
                                <RouteDateTimeText>
                                  {formatDepartureTimeLocal(departureDateTime, fromAirport.timezoneOffset || 0)}
                                </RouteDateTimeText>
                              </RouteDateTimeInfo>
                            )}
                          </RouteAirportCode>
                        )}
                        
                        {/* Arrow with pax below */}
                        <RouteArrowWithPax>
                          <RouteArrowWrapper>
                            <RouteArrowIcon/>
                          </RouteArrowWrapper>
                          <RoutePaxLabel>
                            {leg.pax ?? searchParams.pax ?? 0} {intl.formatMessage({id: 'newTripRequestPage.pax', defaultMessage: 'PAX'})}
                          </RoutePaxLabel>
                        </RouteArrowWithPax>
                        
                        {/* Destination airport - show departure time for next leg if it exists */}
                        {searchParams.multilegRoutes && index < searchParams.multilegRoutes.length - 1 ? (
                          // This is not the last leg, so the destination becomes the origin of the next leg
                          (() => {
                            const nextLeg = searchParams.multilegRoutes?.[index + 1];
                            if (!nextLeg) {
                              return (
                                <RouteAirportCode>
                                  <RouteAirportCodeText>{toAirport.code}</RouteAirportCodeText>
                                </RouteAirportCode>
                              );
                            }
                            const nextDepartureDateTime = parseDepartureDateTime(nextLeg.departureDate, nextLeg.departureTime);
                            // Get previous leg's departure date for comparison
                            const previousDepartureDateTime = departureDateTime;
                            return (
                              <RouteAirportCode>
                                <RouteAirportCodeText>{toAirport.code}</RouteAirportCodeText>
                                {nextDepartureDateTime && (
                                  <RouteDateTimeInfo>
                                    <RouteDateTimeText>
                                      {formatDepartureTimeWithDate(nextDepartureDateTime, previousDepartureDateTime, toAirport.timezoneOffset || 0)}
                                    </RouteDateTimeText>
                                    <RouteDateTimeText>
                                      {formatDepartureTimeLocal(nextDepartureDateTime, toAirport.timezoneOffset || 0)}
                                    </RouteDateTimeText>
                                  </RouteDateTimeInfo>
                                )}
                              </RouteAirportCode>
                            );
                          })()
                        ) : (
                          // Last leg - destination has no departure time
                          <RouteAirportCode>
                            <RouteAirportCodeText>{toAirport.code}</RouteAirportCodeText>
                          </RouteAirportCode>
                        )}
                      </React.Fragment>
                    );
                  })}
                </RouteLine>
              ) : (
                // Display single leg in one line: A → B
                (() => {
                  const departureDateTime = parseDepartureDateTime(searchParams?.departureDate, searchParams?.departureTime);
                  return (
                    <RouteLine>
                      <RouteAirportCode>
                        <RouteAirportCodeText>{airportFromData.code}</RouteAirportCodeText>
                        {departureDateTime && (
                          <RouteDateTimeInfo>
                            <RouteDateTimeText>
                              {formatDepartureTimeUTC(departureDateTime)}
                            </RouteDateTimeText>
                            <RouteDateTimeText>
                              {formatDepartureTimeLocal(departureDateTime, airportFromData.timezoneOffset || 0)}
                            </RouteDateTimeText>
                          </RouteDateTimeInfo>
                        )}
                      </RouteAirportCode>
                      
                      <RouteArrowWithPax>
                        <RouteArrowWrapper>
                          <RouteArrowIcon/>
                        </RouteArrowWrapper>
                        <RoutePaxLabel>
                          {searchParams?.pax ?? 0} {intl.formatMessage({id: 'newTripRequestPage.pax', defaultMessage: 'PAX'})}
                        </RoutePaxLabel>
                      </RouteArrowWithPax>
                      
                      <RouteAirportCode>
                        <RouteAirportCodeText>{airportToData.code}</RouteAirportCodeText>
                      </RouteAirportCode>
                    </RouteLine>
                  );
                })()
              )}
            </ItineraryRouteSection>
          </ItineraryContent>
        </ItineraryCard>

        <MessageSection>
          <CustomFieldInput
            label={intl.formatMessage({id: 'newTripRequestPage.template', defaultMessage: 'Template'})}
          >
            <DebounceSelect
              fetchOptions={async (search: string) => {
                const templates = await fetchMessageTemplates(search);
                // Сохраняем загруженные опции для последующего поиска по value
                setTemplateOptions(templates);
                return templates;
              }}
              placeholder={intl.formatMessage({id: 'newTripRequestPage.selectTemplate', defaultMessage: 'Select template'})}
              showSearch={true}
              value={selectedTemplate}
              onChange={handleTemplateChange}
              allowClear={true}
              labelInValue={true}
              virtual={false}
            />
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'newTripRequestPage.message', defaultMessage: 'Message'})}
            className="message-field"
          >
            <StyledTextArea
              placeholder={intl.formatMessage({id: 'newTripRequestPage.yourMessage', defaultMessage: 'Your message'})}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 8 }}
              style={{ resize: 'none' }}
            />
          </CustomFieldInput>

          <CheckboxWrapper>
            <Checkbox
              checked={sendBcc}
              onChange={(event) => setSendBcc(event.target.checked)}
            >
              {intl.formatMessage({id: 'newTripRequestPage.sendBcc', defaultMessage: 'Send a copy to email'})}
            </Checkbox>
          </CheckboxWrapper>

          <SendButtonWrapper>
            <CustomButton
              type="primary"
              onClick={handleSend}
              disabled={!messageText.trim() || isSubmitting}
              loading={isSubmitting}
              block
            >
              {intl.formatMessage({id: 'newTripRequestPage.send', defaultMessage: 'Send'})}
            </CustomButton>
          </SendButtonWrapper>
        </MessageSection>
      </ModalContent>
    </StyledModal>
    </>
  );
};

